import type { ScaleHubCard, ScaleHubProgress, ScaleHubSection } from "./scaleHubTypes";

export const SCALE_HUB_SECTION_LABELS: Record<ScaleHubSection, string> = {
  missions: "Misiones",
  caged: "CAGED",
};

/** Completar las 3 misiones desbloquea el bloque CAGED. */
export const SCALE_MISSION_UNLOCK_IDS = [
  "mission-l1-recorrido",
  "mission-l2-recorrido",
  "mission-l3-recorrido",
] as const;

export const SCALE_HUB_STATIC_CARDS: ScaleHubCard[] = [
  {
    id: "mission-l1-recorrido",
    section: "missions",
    title: "Nivel 1 — Primeros 5 trastes",
    subtitle: "Do mayor · recorrido lineal",
    description:
      "Tu primer recorrido en el mástil. Escucha la escala de Do mayor en los primeros trastes y observa los colores de cada grado.",
    instructions: [
      "Pulsa Reproducir y escucha el recorrido completo.",
      "Sigue la nota activa en el mástil.",
      "Completa cuando termine la reproducción.",
    ],
    tags: ["Nivel 1", "Mayor", "Recorrido"],
    timeEstimateMin: 4,
    studioKind: "scale-recorrido",
    payload: {
      root: "C",
      scaleType: "major",
      maxFret: 5,
      octaves: 1,
      direction: "asc",
      bpm: 56,
    },
  },
  {
    id: "mission-l2-recorrido",
    section: "missions",
    title: "Nivel 2 — Hasta el 8vo traste",
    subtitle: "Sol mayor · dos octavas",
    description:
      "Amplía el rango hasta el traste 8. La escala de Sol mayor recorre dos octavas ascendentes.",
    instructions: [
      "Reproduce el recorrido y observa cómo cambia de cuerda.",
      "Fíjate en la tónica (rojo) en distintas posiciones.",
      "Completa al finalizar la reproducción.",
    ],
    tags: ["Nivel 2", "Mayor", "Recorrido"],
    timeEstimateMin: 5,
    studioKind: "scale-recorrido",
    payload: {
      root: "G",
      scaleType: "major",
      maxFret: 8,
      octaves: 2,
      direction: "asc",
      bpm: 60,
    },
  },
  {
    id: "mission-l3-recorrido",
    section: "missions",
    title: "Nivel 3 — Ida y vuelta",
    subtitle: "Re mayor · recorrido completo",
    description:
      "Recorrido de ida y vuelta en Re mayor. Prepara el terreno para conectar cajas CAGED en todo el mástil.",
    instructions: [
      "Escucha la subida y la bajada de la escala.",
      "Observa la geometría en el mástil completo.",
      "Completa cuando termine el ciclo.",
    ],
    tags: ["Nivel 3", "Mayor", "Recorrido"],
    timeEstimateMin: 5,
    studioKind: "scale-recorrido",
    payload: {
      root: "D",
      scaleType: "major",
      maxFret: 12,
      octaves: 1,
      direction: "both",
      bpm: 64,
    },
  },

  {
    id: "caged-pattern-e",
    section: "caged",
    title: "Dominio CAGED — Patrón E",
    subtitle: "Do mayor · forma E",
    description:
      "La caja E es la más grave del sistema CAGED. Tónica en 6ª y 4ª cuerda. Estudia su forma antes de explorar transiciones.",
    instructions: [
      "Observa la caja resaltada en el mástil.",
      "Reproduce las notas del patrón.",
      "Completa cuando hayas escuchado el patrón entero.",
    ],
    tags: ["CAGED", "Forma E", "Mayor"],
    timeEstimateMin: 4,
    studioKind: "caged-pattern",
    unlockRequires: [...SCALE_MISSION_UNLOCK_IDS],
    payload: { patternNumber: 1, root: "C", scaleType: "major", maxFret: 15 },
  },
  {
    id: "caged-pattern-d",
    section: "caged",
    title: "Dominio CAGED — Patrón D",
    subtitle: "Do mayor · forma D",
    description: "Caja D: tónica en 4ª y 2ª cuerda. Conecta visualmente con la forma E.",
    instructions: [
      "Identifica las tónicas del patrón.",
      "Reproduce el recorrido de la caja.",
      "Completa al terminar.",
    ],
    tags: ["CAGED", "Forma D", "Mayor"],
    timeEstimateMin: 4,
    studioKind: "caged-pattern",
    unlockRequires: [...SCALE_MISSION_UNLOCK_IDS],
    payload: { patternNumber: 2, root: "C", scaleType: "major", maxFret: 15 },
  },
  {
    id: "caged-pattern-c",
    section: "caged",
    title: "Dominio CAGED — Patrón C",
    subtitle: "Do mayor · forma C",
    description: "Caja C: tónica en 2ª y 5ª cuerda. Forma compacta en posición media.",
    instructions: [
      "Compara el tamaño con las formas E y D.",
      "Reproduce las notas del patrón.",
      "Completa al terminar.",
    ],
    tags: ["CAGED", "Forma C", "Mayor"],
    timeEstimateMin: 4,
    studioKind: "caged-pattern",
    unlockRequires: [...SCALE_MISSION_UNLOCK_IDS],
    payload: { patternNumber: 3, root: "C", scaleType: "major", maxFret: 15 },
  },
  {
    id: "caged-pattern-a",
    section: "caged",
    title: "Dominio CAGED — Patrón A",
    subtitle: "Do mayor · forma A",
    description: "Caja A: tónica en 5ª y 3ª cuerda. Puente hacia registros agudos.",
    instructions: [
      "Localiza la tónica en ambas cuerdas ancla.",
      "Reproduce el patrón completo.",
      "Completa al terminar.",
    ],
    tags: ["CAGED", "Forma A", "Mayor"],
    timeEstimateMin: 4,
    studioKind: "caged-pattern",
    unlockRequires: [...SCALE_MISSION_UNLOCK_IDS],
    payload: { patternNumber: 4, root: "C", scaleType: "major", maxFret: 15 },
  },
  {
    id: "caged-pattern-g",
    section: "caged",
    title: "Dominio CAGED — Patrón G",
    subtitle: "Do mayor · forma G",
    description: "Caja G: tónica en 3ª y 6ª cuerda. Cierra el ciclo CAGED en Do mayor.",
    instructions: [
      "Visualiza cómo G conecta de vuelta con E.",
      "Reproduce el patrón.",
      "Completa al terminar.",
    ],
    tags: ["CAGED", "Forma G", "Mayor"],
    timeEstimateMin: 4,
    studioKind: "caged-pattern",
    unlockRequires: [...SCALE_MISSION_UNLOCK_IDS],
    payload: { patternNumber: 5, root: "C", scaleType: "major", maxFret: 15 },
  },
];

export function getScaleHubCardById(id: string): ScaleHubCard | undefined {
  return SCALE_HUB_STATIC_CARDS.find((c) => c.id === id);
}

export function getScaleHubCardsBySection(section: ScaleHubSection): ScaleHubCard[] {
  return SCALE_HUB_STATIC_CARDS.filter((c) => c.section === section);
}

export function isScaleCardUnlocked(card: ScaleHubCard, progress: ScaleHubProgress): boolean {
  if (!card.unlockRequires?.length) return true;
  return card.unlockRequires.every((id) => progress.completedCardIds.includes(id));
}

export function countScaleHubCards(): number {
  return SCALE_HUB_STATIC_CARDS.length;
}

export function areCagedCardsUnlocked(progress: ScaleHubProgress): boolean {
  return SCALE_MISSION_UNLOCK_IDS.every((id) => progress.completedCardIds.includes(id));
}
